import React from 'react'
import PropTypes from 'prop-types'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Paper, Grid, Box } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { HorizontalBar, Pie, Chart } from 'react-chartjs-2'

import { withStyles } from '@material-ui/core/styles'
import { styles } from '../../classes/Styles'
import { AGE_GENDER_DEMOGRAPHICS } from '../../constants/module_info'

export const HomeDemographicsComponent = props => {
  const { classes, demographics, homepage, screenWidth } = props

  return (
    <Paper className={classes.paper}>
      <Grid container spacing={1} direction="column">
        <Grid item>
          <span className={classes.cardTitle}>Age and Gender Demographics</span>
          <OverlayTrigger overlay={<Tooltip id="tooltip">{AGE_GENDER_DEMOGRAPHICS}</Tooltip>} placement="right">
            <img className={classes.cardInfo} src="/static/images/info.png" alt="info"/>
          </OverlayTrigger>
        </Grid>
        <Grid item className={classes.cardSubTitle}>The baseline demographics of {homepage} engagers</Grid>
      </Grid>
      { (demographics && demographics.isFetching) &&
      <div className='loadingProgress'>
        <CircularProgress />
      </div>
          }
      { (demographics && demographics.details) &&
      <Grid container spacing={1}>
        <Grid item xl={5} lg={5} md={5} sm={5} xs={12}>
          <GenderDemographics
            demo={demographics}
            classes={classes}
          />
        </Grid>
        <Grid item xl={7} lg={7} md={7} sm={7} xs={12} >
          <AgeDemographics
            demo={demographics}
            classes={classes}
            screenWidth={screenWidth}
            />
        </Grid>
      </Grid>
}
    </Paper>
  )
}

HomeDemographicsComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  homepage: PropTypes.string,
  demographics: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.object,
    details: PropTypes.object
  }),
  screenWidth: PropTypes.number
}

export const GenderDemographics = ({classes, demo}) => {
  if (!demo.details) {
    return null
  }

  const male = demo.details.num_male
  const female = demo.details.num_female
  const data = {
    labels: [
      `Male (${male}%)`,
      `Female (${female}%)`
    ],
    datasets: [{
      data: [male, female],
      backgroundColor: [
        '#4089fb',
        '#00b5ee'
      ],
      borderWidth: 0
    }]
  }
  const options = {
    maintainAspectRatio: true,
    responsive: true,
    weight: 1,
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        pointStyle: 'circle',
        usePointStyle: true
      },
      display: false
    },
    tooltips: {
      enabled: false
    },
    cutoutPercentage: 75
  }

  return (
    <Grid item container direction="row" justify="space-between" alignItems="center" spacing={1} className='gender fullHeight demoBorderRight'>
      <Grid item className={classes.demoGenderTitle}>
        <span>Gender</span>
      </Grid>
      <Grid item container direction="column" justify="space-between" alignItems="center" spacing={1}>
        <Grid item className={classes.demoGenderChart}>
          <Box className="pieChart">
            <Pie data={data} options={options}/>
          </Box>
        </Grid>
        <Grid container spacing={2} item direction="row" justify="center" alignItems="center" className={classes.demoGenderLegent}>
          <span className={classes.demoGender}><span className={`${classes.demoGenderLegentCircle} ${classes.demoGenderFemaleCircle}`}></span>Female ({female}%)</span>
          <span className={classes.demoGender}><span className={`${classes.demoGenderLegentCircle} ${classes.demoGenderMaleCircle}`}></span>Male ({male}%)</span>
        </Grid>
      </Grid>
    </Grid>
  )
}

GenderDemographics.propTypes = {
  classes: PropTypes.object.isRequired,
  demo: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.object,
    details: PropTypes.object
  })
}

export class AgeDemographics extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    demo: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      error: PropTypes.object,
      details: PropTypes.object
    }),
    screenWidth: PropTypes.number
  }

  componentDidMount () {
    const fontSize = this.props.screenWidth < 1024 ? 12 : 14
    Chart.pluginService.register({
      afterDraw: function (chart) {
        let ctx = chart.ctx
        ctx.textAlign = 'left'
        ctx.textBaseline = 'center'
        ctx.fillStyle = '#808184'
        ctx.font = `${fontSize}px Uni Sans Regular`
        ctx.fontWeight = 'bold'
        chart.config.data.datasets.forEach(function (dataset) {
          if (dataset.label === 'Age Demographics'){
            const dataArray = dataset.data
            const key = Object.keys(dataset._meta)
            dataset._meta[key].data.forEach(function (bar, index) {
              ctx.fillText(dataArray[index] === 0 ? '<1%' : `${dataArray[index]}%`, bar._view.x + 5, bar._view.y + 5)
            })
          }
        })
      }
    })
  }

  render () {
    const { classes, demo, screenWidth } = this.props
    if (!demo.details) {
      return null
    }

    const ageUnder18 = demo.details.num_age_under18
    const age18To24 = demo.details.num_age18_to24
    const age25To34 = demo.details.num_age25_to34
    const age35To54 = demo.details.num_age35_to54
    const age55Plus = demo.details.num_age55_plus
    const maxAge = Math.max(ageUnder18, age18To24, age25To34, age35To54, age55Plus)
    const maxAgeValue = Math.ceil(maxAge/10) * 10 + 10
    const fontSize = screenWidth < 1024 ? 12 : 14
    // const responsive = screenWidth >= 1024 && screenWidth < 1440 ? false : true
    // const maintainAspectRatio = screenWidth >= 1024 && screenWidth < 1440 ? false : true
    const data = {
      labels: ['<18', '18-24', '25-34', '35-54', '55+'],
      datasets: [
        {
          label: 'Age Demographics',
          backgroundColor: 'rgba(161, 109, 196, 1)',
          borderColor: 'rgba(161, 109, 196, 1)',
          data: [ageUnder18, age18To24, age25To34, age35To54, age55Plus],
          barPercentage: 1
        }
      ]
    }

    const options = {
      maintainAspectRatio: true,
      responsive: true,
      legend: {
        display: false
      },
      tooltips: {
        enabled: false
      },
      scales: {
        yAxes: [{
          ticks: {
            fontSize: fontSize,
            fontColor: '#808184',
            fontFamily: 'Uni Sans Regular'
          },
          gridLines: {
            display: false,
            drawBorder: false
          }
        }],
        xAxes: [{
          position: 'right',
          gridLines: {
            drawBorder: false,
            color: '#E6E7E8'
          },
          ticks: {
            fontSize: fontSize,
            fontColor: '#E6E7E8',
            max: maxAgeValue,
            min: 0,
            stepSize: 10,
            callback: function (label) {
              return (label === 0 || label ===20 || label === 40 || label === 60) ? `${label}%` : ''
              // return `${label}%`
            }
          }
        }]
      }
    }
    return (
      <Grid item container direction="column" justify="center" spacing={1} className='homepageDemo'>
        <Grid item className={classes.demoGenderTitle}>
          <span>Age</span>
        </Grid>
        <Grid item className={classes.ageChart}>
          <Box className="barChart">
            <HorizontalBar data={data} options={options}/>
          </Box>
        </Grid>
      </Grid>
    )
  }
}

export default withStyles(styles)(HomeDemographicsComponent)